import { useEffect, useState } from 'react';
import { collectionUrl } from '@/lib/collectionUrl';
import { canonicalPath } from '@workspace/url-source';
import type { MenuItem } from '@/lib/shopifyApi.eager';
import { KWIATY_SECTIONS, OKAZJE_SECTIONS, POGRZEB_SECTIONS, KREATOR_ITEMS, POGRZEB_ITEMS, BUKIETY_PRODUCTS, KWIATY_FEATURED, OKAZJE_FEATURED, type MegaSection } from './megaMenuData';

export type NavigationLink = { id: string; label: string; href: string; icon?: string };
export type NavigationSection = { title: string; items: NavigationLink[] };
export type MobileNavigationItem = NavigationLink | { id: string; label: string; href?: string; children: NavigationLink[] };
export type NavigationModel = {
  topLevel: MenuItem[];
  mega: {
    kwiaty: MegaSection[]; okazje: MegaSection[]; pogrzeb: MegaSection[];
    kreatorItems: typeof KREATOR_ITEMS; pogrzebItems: typeof POGRZEB_ITEMS;
    bukietyProducts: typeof BUKIETY_PRODUCTS;
    kwiatyFeatured: typeof KWIATY_FEATURED; okazjeFeatured: typeof OKAZJE_FEATURED;
  };
  mobile: MobileNavigationItem[];
  categories: NavigationSection[];
};

export const NAV_FALLBACK: MenuItem[] = [
  { title: 'Kwiaty', url: collectionUrl('kwiaty'), type: 'CATALOG', items: [
    { title: 'Róże', url: collectionUrl('roze'), type: 'CATALOG' },
    { title: 'Tulipany', url: collectionUrl('tulipany'), type: 'CATALOG' },
    { title: 'Lilie', url: collectionUrl('lilie'), type: 'CATALOG' },
    { title: 'Gerbery', url: collectionUrl('gerbery'), type: 'CATALOG' },
    { title: 'Goździki', url: collectionUrl('gozdziki'), type: 'CATALOG' },
    { title: 'Słoneczniki', url: collectionUrl('sloneczniki'), type: 'CATALOG' },
    { title: 'Kwiaty w torebce', url: collectionUrl('kwiaty-w-torebce'), type: 'CATALOG' },
    { title: 'Kosze kwiatowe', url: collectionUrl('kosze-kwiatowe'), type: 'CATALOG' },
  ] },
  { title: 'Okazje', url: collectionUrl('okazje'), type: 'CATALOG', items: [] },
  { title: 'Pogrzeb', url: collectionUrl('pogrzeb'), type: 'CATALOG', items: [] },
  { title: 'Kreator', url: canonicalPath('/kreatory-bukietow'), type: 'CATALOG', items: [] },
  { title: 'Ślub', url: canonicalPath('/slub'), type: 'CATALOG', items: [] },
  { title: 'Blog', url: canonicalPath('/blog'), type: 'PAGE', items: [] },
  { title: 'Kontakt', url: canonicalPath('/kontakt'), type: 'PAGE', items: [] },
];

const CATEGORY_SECTIONS: NavigationSection[] = [
  { title: 'Kwiaty i prezenty', items: [
    ['bukiety', 'Bukiety', collectionUrl('bukiety')],
    ['flowerbox', 'Flowerboxy', collectionUrl('flower-boxy')],
    ['roze', 'Róże', collectionUrl('roze')],
    ['doniczkowe', 'Rośliny doniczkowe', collectionUrl('kwiaty-doniczkowe')],
    ['balony', 'Balony z helem', collectionUrl('balony-z-helem')],
    ['kosze', 'Kosze kwiatowe', collectionUrl('kosze-kwiatowe')],
    ['torebki', 'Kwiaty w torebce', collectionUrl('kwiaty-w-torebce')],
    ['lasy', 'Lasy w szkle', collectionUrl('lasy-w-szkle')],
    ['wieczne-roze', 'Wieczne róże', collectionUrl('wieczne-roze')],
    ['slub', 'Bukiety ślubne', collectionUrl('slub')],
    ['bestsellery', 'Bestsellery', collectionUrl('bestsellery')],
    ['kwiaty', 'Wszystkie kwiaty', collectionUrl('kwiaty')],
  ].map(([id, label, href]) => ({ id, label, href, icon: id })) },
  { title: 'Na okazję', items: [
    ['urodziny', 'Urodziny', collectionUrl('urodziny')],
    ['imieniny', 'Imieniny', collectionUrl('imieniny')],
    ['rocznica', 'Rocznica', collectionUrl('rocznica')],
    ['podziekowania', 'Podziękowania', collectionUrl('podziekowania')],
    ['przeprosiny', 'Przeprosiny', collectionUrl('przeprosiny')],
    ['gratulacje', 'Gratulacje', collectionUrl('gratulacje')],
    ['niespodzianka', 'Niespodzianka', collectionUrl('niespodzianka')],
    ['zareczyny', 'Zaręczyny', collectionUrl('zareczyny')],
    ['komunia', 'Komunia', collectionUrl('komunia')],
    ['obrona', 'Obrona pracy', collectionUrl('obrona')],
  ].map(([id, label, href]) => ({ id, label, href, icon: id })) },
  { title: 'Dla kogo', items: [
    ['dlaniej', 'Dla Niej', collectionUrl('dla-dziewczyny')],
    ['dlamamy', 'Dla mamy', collectionUrl('dla-mamy')],
    ['dlazony', 'Dla żony', collectionUrl('dla-zony')],
    ['dlababci', 'Dla babci', collectionUrl('dla-babci')],
    ['dlaniego', 'Dla niego', collectionUrl('dla-mezczyzny')],
    ['dlaprzyjaciela', 'Dla przyjaciela', collectionUrl('dla-przyjaciela')],
    ['dlaparymlodej', 'Dla Pary Młodej', collectionUrl('dla-pary-mlodej')],
  ].map(([id, label, href]) => ({ id, label, href, icon: id === 'dlaparymlodej' ? 'slub' : id === 'dlaniego' || id === 'dlaprzyjaciela' ? 'dlaniego' : 'dlaniej' })) },
  { title: 'Kompozycje pogrzebowe', items: [
    ['pogrzeb', 'Wszystkie kompozycje', collectionUrl('pogrzeb')],
    ['wience', 'Wieńce pogrzebowe', collectionUrl('wience-pogrzebowe')],
    ['wiazanki', 'Wiązanki pogrzebowe', collectionUrl('wiazanki-pogrzebowe')],
    ['bukiety-pogrzebowe', 'Bukiety pogrzebowe', collectionUrl('bukiety-pogrzebowe')],
    ['sztuczne', 'Sztuczne kompozycje', collectionUrl('sztuczne-kompozycje')],
  ].map(([id, label, href]) => ({ id, label, href, icon: 'pogrzeb' })) },
  { title: 'Święta', items: [
    ['dzienbabci', 'Dzień Babci i Dziadka', collectionUrl('dzien-babci-i-dziadka')],
    ['walentynki', 'Walentynki', collectionUrl('walentynki')],
    ['dzienkobiet', 'Dzień Kobiet', collectionUrl('dzien-kobiet')],
    ['wielkanoc', 'Wielkanoc', collectionUrl('wielkanoc')],
    ['dzienmatki', 'Dzień Matki', collectionUrl('dzien-matki')],
    ['dziendziecka', 'Dzień Dziecka', collectionUrl('dzien-dziecka')],
    ['dzienojca', 'Dzień Ojca', collectionUrl('dzien-ojca')],
    ['wszystkichswietych', 'Wszystkich Świętych', collectionUrl('wszystkich-swietych')],
    ['bozenarodzenie', 'Boże Narodzenie', collectionUrl('boze-narodzenie')],
  ].map(([id, label, href]) => ({ id, label, href, icon: 'okazje' })) },
];

const hiddenTitles = new Set(['Kosze kwiatowe', 'Kosze w torebce', 'Bukiety', 'Flower Boxy', 'Flowerboxy', 'FlowerBoxy', 'Dostawa', 'Kwiaty doniczkowe']);
export function normalizeCachedNavigation(items: MenuItem[]): MenuItem[] {
  const filtered = normalizeShopifyNavigation(items);
  const kwiaty = filtered.findIndex(item => item.title === 'Kwiaty');
  if (kwiaty < 0) filtered.unshift({ title: 'Kwiaty', url: collectionUrl('kwiaty'), type: 'CATALOG', items: [] });
  else filtered[kwiaty] = { ...filtered[kwiaty], url: collectionUrl('kwiaty') };
  if (!filtered.some(item => item.title === 'Okazje')) filtered.splice(filtered.findIndex(item => item.title === 'Kwiaty') + 1, 0, { title: 'Okazje', url: collectionUrl('okazje'), type: 'CATALOG', items: [] });
  return filtered;
}

export function normalizeShopifyNavigation(items: MenuItem[]): MenuItem[] {
  const pageMap: Record<string, string> = { 'o-nas': canonicalPath('/o-nas'), blog: canonicalPath('/blog'), kontakt: canonicalPath('/kontakt'), dostawa: canonicalPath('/dostawa'), regulamin: canonicalPath('/regulamin'), faq: canonicalPath('/faq'), platnosci: canonicalPath('/platnosci'), 'polityka-prywatnosci': canonicalPath('/prywatnosc'), 'polityka-zwrotow': canonicalPath('/polityka-zwrotow'), 'dla-firm': canonicalPath('/dla-firm'), 'warsztaty-florystyczne': canonicalPath('/warsztaty'), 'zamowienie-specjalne': canonicalPath('/zamowienie-specjalne') };
  const normalizeUrl = (url: string, title: string) => {
    const slug = title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
    try {
      const path = new URL(url, 'https://bukietowa.pl').pathname;
      const page = path.match(/^\/pages\/([^/]+)/);
      if (page) return pageMap[page[1]] ?? canonicalPath(`/${page[1]}`);
      const collection = path.match(/^\/collections\/([^/]+)/);
      if (collection) return collectionUrl(collection[1]);
      if (pageMap[slug]) return pageMap[slug];
      return canonicalPath(path);
    } catch {
      return pageMap[slug] ?? url;
    }
  };
  const normalizeItem = (item: MenuItem): MenuItem => ({
    ...item,
    url: normalizeUrl(item.url, item.title),
    title: item.title === 'Kreatory' ? 'Kreator' : item.title,
    items: item.items?.map(normalizeItem) ?? [],
  });
  const filtered = items.map(normalizeItem).filter(item => !hiddenTitles.has(item.title));
  const kwiaty = filtered.findIndex(item => item.title === 'Kwiaty');
  if (kwiaty < 0) filtered.unshift({ title: 'Kwiaty', url: collectionUrl('kwiaty'), type: 'CATALOG', items: [] });
  else filtered[kwiaty] = { ...filtered[kwiaty], url: collectionUrl('kwiaty') };
  if (!filtered.some(item => item.title === 'Okazje')) filtered.splice(filtered.findIndex(item => item.title === 'Kwiaty') + 1, 0, { title: 'Okazje', url: collectionUrl('okazje'), type: 'CATALOG', items: [] });
   if (!filtered.some(item => item.url === canonicalPath('/slub') || item.title === 'Ślub')) { const index = filtered.findIndex(item => item.title === 'Kreator'); filtered.splice(index === -1 ? filtered.length : index + 1, 0, { title: 'Ślub', url: canonicalPath('/slub'), type: 'CATALOG', items: [] }); }
  return filtered;
}

export function createNavigationModel(topLevel: MenuItem[]): NavigationModel {
  const hrefFor = (title: string, fallback: string) => topLevel.find(item => item.title === title)?.url ?? fallback;
  const group = (id: string, label: string, section: MegaSection) => ({ id, label, href: section.href, children: section.items.map(item => ({ id: item.label, label: item.label, href: item.href, icon: item.icon })) });
  return { topLevel, mega: { kwiaty: KWIATY_SECTIONS, okazje: OKAZJE_SECTIONS, pogrzeb: POGRZEB_SECTIONS, kreatorItems: KREATOR_ITEMS, pogrzebItems: POGRZEB_ITEMS, bukietyProducts: BUKIETY_PRODUCTS, kwiatyFeatured: KWIATY_FEATURED, okazjeFeatured: OKAZJE_FEATURED }, categories: CATEGORY_SECTIONS, mobile: [
    { id: 'kwiaty', label: 'Kwiaty', href: hrefFor('Kwiaty', collectionUrl('kwiaty')) }, { id: 'bukiety', label: 'Bukiety', href: collectionUrl('bukiety') }, group('inne', 'Inne', KWIATY_SECTIONS.find(section => section.id === 'inne')!), { id: 'bestsellery', label: 'Bestsellery', href: collectionUrl('bestsellery') }, { id: 'okazje', label: 'Okazje', href: hrefFor('Okazje', collectionUrl('okazje')) }, group('pogrzeb', 'Pogrzeb', POGRZEB_SECTIONS[0]), { id: 'kreator', label: 'Kreator bukietu', href: hrefFor('Kreator', canonicalPath('/kreatory-bukietow')) }, { id: 'slub', label: 'Ślub', href: hrefFor('Ślub', canonicalPath('/slub')) }, { id: 'blog', label: 'Blog', href: hrefFor('Blog', canonicalPath('/blog')) }, { id: 'kontakt', label: 'Kontakt', href: hrefFor('Kontakt', canonicalPath('/kontakt')) },
  ] };
}

export function publishNavigation(topLevel: MenuItem[]) {
  try { localStorage.setItem('bk_nav_menu', JSON.stringify(topLevel)); } catch { /* storage unavailable */ }
  window.dispatchEvent(new CustomEvent<MenuItem[]>('bk:navigation-update', { detail: topLevel }));
}
export function useSharedNavigationModel() {
  const initial = () => { try { const stored = localStorage.getItem('bk_nav_menu'); return createNavigationModel(stored ? normalizeCachedNavigation(JSON.parse(stored)) : NAV_FALLBACK); } catch { return createNavigationModel(NAV_FALLBACK); } };
  const [model, setModel] = useState<NavigationModel>(initial);
  useEffect(() => { const update = (event: Event) => setModel(createNavigationModel((event as CustomEvent<MenuItem[]>).detail)); window.addEventListener('bk:navigation-update', update); return () => window.removeEventListener('bk:navigation-update', update); }, []);
  return model;
}
